import { portalEn, portalRu } from '@cc/schema';
import type { WorksheetFieldData } from './hubModel';

/**
 * E34 · the worksheet PRINT VIEW's pure part (Q-paper-2 A, the founder 2026-10-03; cc-homework, chapter "Worksheets —
 * Templates & library (portal, E34)", Print view): how each field type is drawn on paper, and the route the Print
 * actions open. A single-line field is a rule, a long text a block of lines, a 0–N scale a row of numbers to circle,
 * single and multiple choice their options as tick boxes, a date a blank date line, a checkbox a tick box. The client's
 * name is NEVER on the sheet (privacy) — a "Name: ______" fill-in line only. No i18n here.
 */

export type PrintField =
  | { kind: 'rule'; short: boolean }
  | { kind: 'lines'; count: number }
  | { kind: 'scale'; values: number[] }
  | { kind: 'ticks'; options: string[] }
  | { kind: 'tick' };

/** The most numbers a scale prints as a row to circle; a wider range is a blank to write the number in. */
export const MAX_SCALE_STEPS = 21;
/** The ruled lines a long-text answer gets. */
export const LONG_TEXT_LINES = 6;

export const printFieldOf = (
  f: Pick<WorksheetFieldData, 'type' | 'options' | 'min' | 'max'>,
): PrintField => {
  switch (f.type) {
    case 'text':
      return { kind: 'rule', short: false };
    case 'longText':
      return { kind: 'lines', count: LONG_TEXT_LINES };
    case 'number':
    case 'date':
      return { kind: 'rule', short: true };
    case 'scale': {
      const min = Number.isFinite(f.min) ? Math.ceil(f.min as number) : 0;
      const max = Number.isFinite(f.max) ? Math.floor(f.max as number) : 10;
      if (max < min || max - min + 1 > MAX_SCALE_STEPS) return { kind: 'rule', short: true };
      return { kind: 'scale', values: Array.from({ length: max - min + 1 }, (_, i) => min + i) };
    }
    case 'singleChoice':
    case 'multiChoice': {
      const options = (f.options ?? []).filter((o) => (o ?? '').trim());
      return options.length > 0 ? { kind: 'ticks', options } : { kind: 'rule', short: false };
    }
    case 'checkbox':
      return { kind: 'tick' };
    default:
      return { kind: 'rule', short: false };
  }
};

/** Where the Print actions open the print view: the library prints the latest version, the hub the one the assignment pinned. */
export const printPathOf = (templateId: string, version?: number | null): string =>
  `/worksheets/${encodeURIComponent(templateId)}/print${version ? `?version=${version}` : ''}`;

/** The version asked in `?version=` — a positive whole number, else null (the latest). */
export const printVersionOf = (raw: string | null | undefined): number | null => {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * The hub row's Print action: an assigned WORKSHEET the client may complete on paper (`paperAllowed` — unscored and not
 * a consent), still live (not withdrawn, not the client's own). The pinned template and version ride on the row's target.
 */
export const hubPrintTargetOf = (row: {
  status: string;
  practiceId?: string | null;
  paperAllowed?: boolean | null;
  target?: { type: string; ref?: string | null; version?: number | null } | null;
}): { templateId: string; version: number | null } | null => {
  if (!row.practiceId || row.status === 'cancelled' || !row.paperAllowed) return null;
  if (row.target?.type !== 'worksheet' || !row.target.ref) return null;
  return { templateId: row.target.ref, version: row.target.version ?? null };
};

/**
 * Q-print-1 A (the founder 2026-10-03): the printed sheet's chrome — the page-1 header's practice line, the footer's site
 * and worksheet name, the "Page X of Y" parts and the return line — in the WORKSHEET's language (`template.language`,
 * en or ru; anything else prints English), never the portal UI's. The client's name is not an input: it is never printed.
 * `pageBefore` / `pageBetween` / `pageAfter` are the text around the two page counters, for the print CSS.
 */
/**
 * Q-print-1 A, refined (founder 2026-10-03): only a worksheet CopingCard itself published (the library: owner 'library',
 * the virtual CopingCard practice '1') wears the CopingCard logo. A practice's own, an imported one and the practice's
 * copy of a library sheet (owner 'practice', `copiedFrom` set) carry no logo — practices may import copyrighted sheets —
 * only the footer text "Prepared by <practice> with CopingCard" ("Prepared with CopingCard" when the practice is unknown).
 */
export const isCopingCardSheet = (t: { owner?: string | null; practiceId?: string | null }): boolean =>
  t.owner === 'library' || t.practiceId === '1';

export type PrintChrome = {
  branded: boolean;
  preparedWith: string | null;
  language: 'en' | 'ru';
  practiceLine: string | null;
  site: string;
  footerName: string;
  returnLine: string;
  pageBefore: string;
  pageBetween: string;
  pageAfter: string;
};

export const printChrome = (
  template: { name: string; language?: string | null; owner?: string | null; practiceId?: string | null },
  practiceName?: string | null,
): PrintChrome => {
  const language = template.language === 'ru' ? 'ru' : 'en';
  const p = (language === 'ru' ? portalRu : portalEn).worksheets.print;
  const practice = (practiceName ?? '').trim();
  const [before, rest = ''] = p.page.split('{{x}}');
  const [between, after = ''] = rest.split('{{y}}');
  const branded = isCopingCardSheet(template);
  return {
    branded,
    preparedWith: branded ? null : practice ? p.preparedWith.replace('{{practice}}', practice) : p.preparedWithTool,
    language,
    practiceLine: practice ? p.practiceHeader.replace('{{name}}', practice) : null,
    site: p.site,
    footerName: template.name,
    returnLine: p.returnLine,
    pageBefore: before,
    pageBetween: between,
    pageAfter: after,
  };
};
